import { useState } from "react";
import { fieldWord } from "@workforceos/shared";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiErrorMessage, apiGet, apiPost, apiPut } from "../../api/client";
import { roleLabel } from "../../lib/roleLabel";
import { formatWhen } from "../../lib/when";

/**
 * Template studio (a04, SRS §7). Each agent is a versioned template. A new
 * version starts as a draft; its evals run as code; Approve stays disabled,
 * with the reason in words, until a passing report exists at the current
 * rule pack. Who may ask for an agent and decide it is set on a draft, and
 * each decision's policy — standing approvals and step-up — is set here.
 * Below the built-in agents: the workspace's composed agents (a Supervisor
 * may retire one, with a reason) and the library's ready-made agents by
 * area, read-only (agent-library Task 14).
 */

type StepUp = "never" | "always" | { above: { field: string; value: number } };
interface Policy {
  decision_key: string;
  label: string;
  approver_role: string;
  step_up_declared: StepUp;
  step_up: StepUp | null;
  auto_apply_enabled: boolean;
  min_matches: number;
  max_age_days: number;
  signature_fields: { field: string; tolerance_pct?: number; equals?: true }[];
  suspended: boolean;
  suspended_reason?: string | null;
  /** False for a decision that must have a person, or that needs a code. */
  auto_apply_allowed?: boolean;
  updated_at: string | null;
}
interface Check {
  key: string;
  passed: boolean;
  state?: "passed" | "failed" | "not_enough_history";
  detail_en: string;
  figures?: Record<string, number>;
}
interface Template {
  hand_key: string;
  name: string;
  version: number;
  status: "draft" | "approved" | "retired";
  eval_stale: boolean;
  description_en: string;
  report: { id: string; passed: boolean; state?: "passed" | "failed" | "not_enough_history"; pack_version: string; created_at: string; as_built: boolean; checks: Check[] } | null;
  approve_blocked: string | null;
  entitlements: { role: string; may_spawn: boolean; may_approve: boolean }[];
  policies: Policy[];
}

const ROLES = ["cxo", "reviewer", "operator", "agent_handler", "approver"];
const CHECK_NAMES: Record<string, string> = {
  E1_determinism: "Same inputs, same figures",
  E2_replay: "Past runs reach the same verdicts",
  E3_routing: "Requests reach the right agent",
  E4_substitution: "Drafts carry only computed figures",
};
const STATUS: Record<Template["status"], string> = { draft: "Draft", approved: "Approved", retired: "Retired" };

function reportWords(t: Template): string {
  if (!t.report) return "No report yet";
  if (t.report.as_built) return "As built";
  if (t.report.passed) return "Evals passed";
  return t.report.state === "not_enough_history" ? "Not enough history yet" : "Evals failed";
}

export function TemplateStudio() {
  const { workspaceId = "" } = useParams();
  const base = `/api/workspaces/${workspaceId}`;
  const q = useQuery({
    queryKey: ["hand-templates", workspaceId],
    queryFn: () => apiGet<{ pack_version: string; codes?: { email_ready: boolean; demo: boolean }; templates: Template[] }>(`${base}/hand-templates`),
  });
  const [open, setOpen] = useState<string | null>(null);
  const templates = q.data?.templates ?? [];
  const selected = templates.find((t) => `${t.hand_key}-${t.version}` === open) ?? null;

  return (
    <div className="main-inner" data-testid="template-studio">
      <div className="greet-row">
        <span className="greet">Template studio</span>
      </div>
      <div className="subgreet">
        <div className="section-eyebrow">AGENTS</div>
        <div className="lede">
          {q.isLoading ? "Reading the agents…" : `Each agent is a version. Rules pack ${q.data?.pack_version ?? "unknown"}.`}
        </div>
      </div>
      {q.isError ? <span className="pill red">{apiErrorMessage(q.error)}</span> : null}

      <div className={`tpl-layout${selected ? " has-detail" : ""}`}>
      <div className="tpl-list" role="list">
        {templates.map((t) => {
          const id = `${t.hand_key}-${t.version}`;
          return (
            <button
              key={id}
              type="button"
              role="listitem"
              className={`play-card tpl-row${open === id ? " active" : ""}`}
              data-testid={`tpl-${id}`}
              onClick={() => setOpen(open === id ? null : id)}
            >
              <span className="play-title">{t.name}</span>
              <span className="play-meta">
                <span>Version {t.version}</span>
                <span className={`pill ${t.status === "approved" ? "green" : t.status === "draft" ? "blue" : ""}`}>{STATUS[t.status]}</span>
                {t.eval_stale ? <span className="pill amber">Evals out of date</span> : null}
                <span className={`pill ${t.report?.passed === false && t.report.state !== "not_enough_history" ? "red" : ""}`}>{reportWords(t)}</span>
              </span>
            </button>
          );
        })}
      </div>
      {selected ? (
        <Detail key={`${selected.hand_key}-${selected.version}`} t={selected} all={templates} base={base} onOpen={setOpen}
          codesReach={(q.data?.codes?.email_ready ?? true) || (q.data?.codes?.demo ?? false)} />
      ) : null}
      </div>

      <ComposedSection base={base} />
    </div>
  );
}

// ── Composed agents and the library (agent-library Task 14) ─────────────────

type ComposedStatus = "draft" | "awaiting_access" | "trial" | "active" | "held" | "retired";
interface Composed {
  id: string;
  hand_key: string;
  name: { en: string; ar: string };
  requester_name: string | null;
  status: ComposedStatus;
  version: number;
  in_words: { en: string; ar: string };
  /** The five access lines, as the requester and the Supervisor saw them. */
  access_summary: { en: string[]; ar: string[] };
  report: { id: string; passed: boolean; state: "passed" | "failed" | "not_enough_history"; version: number; created_at: string; checks: Check[] } | null;
  runs: { total: number; this_version: number };
}
interface LibraryArea {
  area: { key: string; label: { en: string; ar: string } };
  templates: { key: string; name: { en: string; ar: string }; blurb: { en: string; ar: string }; in_words: { en: string; ar: string } }[];
}

const COMPOSED_STATUS: Record<ComposedStatus, { label: string; pill: string }> = {
  trial: { label: "Trial", pill: "amber" },
  active: { label: "Active", pill: "green" },
  held: { label: "Held", pill: "red" },
  retired: { label: "Retired", pill: "" },
  awaiting_access: { label: "Awaiting access", pill: "blue" },
  draft: { label: "Draft", pill: "" },
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function CheckList({ checks, testId }: { checks: Check[]; testId?: string }) {
  return (
    <ul className="tpl-checks" aria-label="Evals" data-testid={testId}>
      {checks.map((c) =>
        c.state === "not_enough_history" ? (
          // Too little history is "not yet", said with what makes history.
          <li key={c.key} data-testid={`eval-${c.key}`} className="wait">
            <b>{CHECK_NAMES[c.key] ?? c.key}</b> — <span className="pill">Not enough history yet ({c.figures?.["recorded"] ?? 0} of {c.figures?.["needed"] ?? 5} runs)</span>{" "}
            Runs are made by the schedule, or when someone asks.
          </li>
        ) : (
          <li key={c.key} data-testid={`eval-${c.key}`} className={c.passed ? "ok" : "bad"}>
            <b>{CHECK_NAMES[c.key] ?? c.key}</b> — <span className={`pill ${c.passed ? "green" : "red"}`}>{c.passed ? "Passed" : "Failed"}</span>{" "}
            {c.detail_en}
          </li>
        ),
      )}
    </ul>
  );
}

function ComposedSection({ base }: { base: string }) {
  const q = useQuery({
    queryKey: ["composed-console", base],
    queryFn: () => apiGet<{ agents?: Composed[]; library?: LibraryArea[] }>(`${base}/composed`),
  });
  const agents = q.data?.agents ?? [];
  const library = q.data?.library ?? [];
  return (
    <>
      <div className="tpl-section" data-testid="composed-agents">
        <div className="section-eyebrow">COMPOSED AGENTS</div>
        {q.isError ? <span className="pill red">{apiErrorMessage(q.error)}</span> : null}
        {q.isLoading ? <div className="play-meta">Reading the composed agents…</div> : null}
        {!q.isLoading && agents.length === 0 ? <div className="play-meta">Nobody has composed an agent yet.</div> : null}
        <div className="tpl-list" role="list">
          {agents.map((a) => (
            <ComposedCard key={a.id} a={a} base={base} />
          ))}
        </div>
      </div>

      <div className="tpl-section" data-testid="library">
        <div className="section-eyebrow">LIBRARY</div>
        <div className="play-meta">The ready-made agents anyone can ask for in the chat, by area. Read-only here.</div>
        {library.map((g) => (
          <div key={g.area.key} className="tpl-library-area">
            <h3 className="play-arch">{g.area.label.en}</h3>
            <ul>
              {g.templates.map((t) => (
                <li key={t.key} data-testid={`library-${t.key}`}>
                  <b>{t.name.en}</b> — <span className="play-meta">{t.in_words.en}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </>
  );
}

function ComposedCard({ a, base }: { a: Composed; base: string }) {
  const qc = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const retire = useMutation({
    mutationFn: () => apiPost(`${base}/composed/${encodeURIComponent(a.id)}/retire`, { reason: reason.trim() }),
    onSuccess: () => {
      setAsking(false);
      void qc.invalidateQueries({ queryKey: ["composed-console"] });
    },
  });
  const id = `composed-${a.id}`;
  const status = COMPOSED_STATUS[a.status] ?? { label: a.status, pill: "" };
  return (
    <div className="play-card tpl-composed" role="listitem" data-testid={id}>
      <div className="play-head">
        <div>
          <div className="play-arch">{a.name.en}</div>
          <div className="play-meta">
            <span>Asked for by {a.requester_name ?? "someone no longer here"}</span>
            <span>Version {a.version}</span>
            <span className={`pill ${status.pill}`} data-testid={`${id}-status`}>{status.label}</span>
          </div>
          <div className="play-meta">{a.in_words.en}</div>
        </div>
      </div>
      <ul className="tpl-access" aria-label="What it may access">
        {a.access_summary.en.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
      {a.report ? (
        <CheckList checks={a.report.checks} testId={`${id}-checks`} />
      ) : (
        <div className="play-meta" data-testid={`${id}-checks`}>Not scored yet.</div>
      )}
      <div className="play-meta">
        {plural(a.runs.total, "run", "runs")}, {a.runs.this_version} of version {a.version}
      </div>
      {a.status !== "retired" ? (
        <div className="tpl-actions">
          {!asking ? (
            <button type="button" className="btn" onClick={() => setAsking(true)}>Retire</button>
          ) : (
            <>
              <label className="tpl-field">
                Why retire it? The person who asked for it is told.
                <input type="text" data-testid={`${id}-reason`} value={reason} maxLength={500}
                  placeholder="A sentence — at least 10 characters." onChange={(e) => setReason(e.target.value)} />
              </label>
              <button type="button" className="btn primary" disabled={reason.trim().length < 10 || retire.isPending} onClick={() => retire.mutate()}>
                Retire this agent
              </button>
              <button type="button" className="btn" onClick={() => setAsking(false)}>Cancel</button>
            </>
          )}
          {retire.isError ? <span className="pill red">{apiErrorMessage(retire.error)}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

function Detail({ t, all, base, onOpen, codesReach }: { t: Template; all: Template[]; base: string; onOpen: (id: string) => void; codesReach: boolean }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["hand-templates"] });
  const path = `${base}/hand-templates/${t.hand_key}`;
  const [checks, setChecks] = useState<Check[] | null>(t.report && !t.report.as_built ? t.report.checks : null);
  const evals = useMutation({
    mutationFn: () => apiPost<{ passed: boolean; checks: Check[] }>(`${path}/${t.version}/evals`, {}),
    onSuccess: (r) => {
      setChecks(r.checks);
      void refresh();
    },
  });
  const approve = useMutation({ mutationFn: () => apiPost(`${path}/${t.version}/approve`, {}), onSuccess: () => void refresh() });
  const draft = useMutation({
    mutationFn: () => apiPost<{ version: number }>(`${path}/draft`, {}),
    onSuccess: async (r) => {
      await refresh();
      onOpen(`${t.hand_key}-${r.version}`);
    },
  });
  const hasDraft = all.some((x) => x.hand_key === t.hand_key && x.status === "draft");

  return (
    <div className="play-card tpl-detail" data-testid="tpl-detail">
      <div className="play-head">
        <div>
          <div className="play-arch">{t.name} · version {t.version}</div>
          <div className="play-meta">{t.description_en}</div>
        </div>
      </div>

      <div className="tpl-actions">
        {t.status === "draft" ? (
          <button type="button" className="btn" data-testid="run-evals" disabled={evals.isPending} onClick={() => evals.mutate()}>
            {evals.isPending ? "Running the evals…" : "Run evals"}
          </button>
        ) : null}
        {t.status === "draft" ? (
          <button type="button" className="btn primary" data-testid="approve" disabled={t.approve_blocked !== null || approve.isPending}
            onClick={() => approve.mutate()}>
            Approve
          </button>
        ) : null}
        {t.approve_blocked ? <span className="play-meta" data-testid="approve-reason">{t.approve_blocked}</span> : null}
        {t.status !== "draft" && !hasDraft ? (
          <button type="button" className="btn" data-testid="new-draft" disabled={draft.isPending} onClick={() => draft.mutate()}>
            New draft
          </button>
        ) : null}
        {[evals, approve, draft].map((m, i) => (m.isError ? <span key={i} className="pill red">{apiErrorMessage(m.error)}</span> : null))}
      </div>

      {checks ? (
        <CheckList checks={checks} />
      ) : t.report?.as_built ? (
        <div className="play-meta">This version was approved as built, before evals existed.</div>
      ) : null}

      {/* Keyed on what was saved, so a refetch shows the stored matrix. */}
      <Entitlements key={JSON.stringify(t.entitlements)} t={t} path={path} onSaved={refresh} />
      {t.policies.map((p) => (
        <PolicyCard key={p.decision_key} handKey={t.hand_key} p={p} base={base} onSaved={refresh} codesReach={codesReach} />
      ))}
    </div>
  );
}

function Entitlements({ t, path, onSaved }: { t: Template; path: string; onSaved: () => void }) {
  const editable = t.status === "draft";
  const initial = () =>
    ROLES.map((role) => t.entitlements.find((e) => e.role === role) ?? { role, may_spawn: false, may_approve: false });
  const [rows, setRows] = useState(initial);
  const save = useMutation({
    mutationFn: () => apiPut(`${path}/${t.version}/entitlements`, { entitlements: rows }),
    onSuccess: onSaved,
  });
  const flip = (role: string, k: "may_spawn" | "may_approve") =>
    setRows((rs) => rs.map((r) => (r.role === role ? { ...r, [k]: !r[k] } : r)));
  return (
    <div className="tpl-section">
      {/* Who may decide is the decision's approver role, set below per decision;
          "may decide" here is stored but not yet enforced, so it is not shown. */}
      <div className="section-eyebrow">WHO MAY ASK FOR IT</div>
      <table className="tpl-matrix">
        <thead>
          <tr>
            <th scope="col">Role</th>
            <th scope="col">May ask</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.role}>
              <th scope="row">{roleLabel(r.role)}</th>
              <td>
                <input type="checkbox" aria-label={`${roleLabel(r.role)} may ask`} data-testid={`ent-${r.role}-spawn`} checked={r.may_spawn}
                  disabled={!editable} onChange={() => flip(r.role, "may_spawn")} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editable ? (
        <button type="button" className="btn" data-testid="save-entitlements" disabled={save.isPending} onClick={() => save.mutate()}>
          Save who may ask
        </button>
      ) : (
        <div className="play-meta">An approved version does not change. Make a new draft to change this.</div>
      )}
      {save.isError ? <span className="pill red">{apiErrorMessage(save.error)}</span> : null}
    </div>
  );
}

function stepUpValue(s: StepUp | null): string {
  if (!s || s === "never") return "never";
  return s === "always" ? "always" : "above";
}

function PolicyCard({ handKey, p, base, onSaved, codesReach }: { handKey: string; p: Policy; base: string; onSaved: () => void; codesReach: boolean }) {
  const [auto, setAuto] = useState(p.auto_apply_enabled);
  const [min, setMin] = useState(p.min_matches);
  const [age, setAge] = useState(p.max_age_days);
  const [fields, setFields] = useState(p.signature_fields);
  const [stepUp, setStepUp] = useState(stepUpValue(p.step_up));
  const declaredAlways = p.step_up_declared === "always";
  const save = useMutation({
    mutationFn: () =>
      apiPut(`${base}/decision-policies/${handKey}/${p.decision_key}`, {
        auto_apply_enabled: auto,
        min_matches: min,
        max_age_days: age,
        signature_fields: fields,
        step_up: stepUp === "above" ? p.step_up : stepUp,
      }),
    onSuccess: onSaved,
  });
  const id = `policy-${p.decision_key}`;
  return (
    <div className="tpl-section" data-testid={id}>
      <div className="section-eyebrow">DECISION · {p.label.toUpperCase()}</div>
      <div className="play-meta">
        Signed off by {roleLabel(p.approver_role)}
        {p.suspended ? (
          <span className="pill amber">
            Suspended after a {p.suspended_reason === "thumbs-down" ? "thumbs-down" : "rejection"}. Save with this on to resume.
          </span>
        ) : null}
        {p.updated_at ? <span>Last changed {formatWhen(p.updated_at)}</span> : null}
      </div>
      <label className="tpl-field">
        <input type="checkbox" data-testid={`${id}-auto`} checked={auto} disabled={p.auto_apply_allowed === false || fields.length === 0}
          onChange={() => setAuto((a) => !a)} />
        Apply my earlier approval when the figures match
      </label>
      {p.auto_apply_allowed !== false && fields.length === 0 ? (
        <div className="play-meta">Choose the figures that must match before earlier approvals can be applied.</div>
      ) : null}
      {p.auto_apply_allowed === false ? (
        <div className="play-meta">This decision always needs a person, or a code, to approve. It is never applied from earlier approvals.</div>
      ) : null}
      <label className="tpl-field">
        After at least
        <input type="number" min={3} max={50} value={min} data-testid={`${id}-min`} onChange={(e) => setMin(Number(e.target.value))} />
        matching approvals, none older than
        <input type="number" min={1} max={365} value={age} data-testid={`${id}-age`} onChange={(e) => setAge(Number(e.target.value))} />
        days
      </label>
      <div className="tpl-field">
        Figures that must match:
        <ul>
          {fields.map((f, i) => (
            <li key={f.field}>
              {fieldWord(f.field, "en")}{" "}
              {f.equals ? (
                "exactly"
              ) : (
                <>
                  within
                  <input type="number" min={0} max={100} value={f.tolerance_pct ?? 0} aria-label={`${fieldWord(f.field, "en")} tolerance`}
                    onChange={(e) => setFields((fs) => fs.map((x, j) => (j === i ? { ...x, tolerance_pct: Number(e.target.value) } : x)))} />
                  %
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
      <label className="tpl-field">
        A code sent to the approver
        <select data-testid={`${id}-stepup`} value={declaredAlways ? "always" : stepUp} disabled={declaredAlways}
          onChange={(e) => setStepUp(e.target.value)}>
          <option value="never">Not needed</option>
          <option value="always" disabled={!codesReach && !declaredAlways}>Always, before approving</option>
          {stepUp === "above" ? <option value="above">Above a figure</option> : null}
        </select>
        {declaredAlways ? <span className="play-meta">Always required for this decision.</span> : null}
        {!codesReach && !declaredAlways ? <span className="play-meta">Connect email first: a code has to reach the approver.</span> : null}
      </label>
      <button type="button" className="btn" data-testid={`${id}-save`} disabled={save.isPending || min < 3} onClick={() => save.mutate()}>
        Save this decision's policy
      </button>
      {save.isSuccess ? <span className="play-meta">Saved.</span> : null}
      {save.isError ? <span className="pill red">{apiErrorMessage(save.error)}</span> : null}
    </div>
  );
}
